"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ReadyToTransformYourProperty() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [countdown, setCountdown] = useState({
    days: 12,
    hours: 6
  });
  useEffect(() => {
    const target = new Date();
    target.setDate(target.getDate() + 12);
    target.setHours(target.getHours() + 6);
    const tick = () => {
      const now = new Date().getTime();
      const diff = target.getTime() - now;
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
        setCountdown({
          days,
          hours
        });
      }
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, []);
  return <section data-section-id="920" id="ready-to-transform-your-property" className="bg-background text-foreground relative overflow-hidden min-h-[720px] flex items-center justify-center px-6 py-24">
      {}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/cleared_land_landscape.png)] animate-[kenburns_30s_ease-in-out_infinite_alternate]" style={{
        backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/cleared_land_landscape.png)"
      }} />
        {}
        <div className="absolute inset-0 !bg-[url(https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/Newsom-after-2.jpg)] !bg-[url(https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/IMG_3569_overlayt.jpg)] !text-[rgba(39,151,39,1)]" style={{
        background: "radial-gradient(ellipse at center, hsla(151, 67%, 6%, 0.7) 0%, hsla(151, 67%, 6%, 0.6) 50%, hsla(151, 67%, 6%, 0.5) 100%)"
      }} />
      </div>

      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }
        @keyframes pulse-amber {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        .pulse-dot {
          animation: pulse-amber 2s ease-in-out infinite;
        }
        .amber-glow:hover {
          box-shadow: 0 0 40px hsla(23, 90%, 55%, 0.5), 0 0 80px hsla(23, 90%, 55%, 0.3);
        }
      `}</style>

      {}
      <div ref={ref} className={`relative z-10 max-w-4xl mx-auto text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="pulse-dot inline-block w-2 h-2 rounded-lg" style={{
          backgroundColor: "var(--primary)"
        }} />
          <span className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase" style={{
          color: "var(--primary)"
        }}>
            Professional Land Clearing Services
          </span>
        </div>

        {}
        <h2 className="font-default font-bold leading-[1.05] mb-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-primary-foreground" style={{
        letterSpacing: "-0.02em"
      }}>
          Your land, <em className="italic font-normal">reclaimed.</em>
          <br />
          Ready for your vision.
        </h2>

        {}
        <p className="font-default text-base sm:text-lg leading-relaxed text-foreground/80 max-w-xl mx-auto mb-12">
          We provide expert forestry mulching and land clearing services to transform overgrown brush into pristine, usable property.
        </p>

        {}
        <div className="flex flex-col items-center gap-5">
          <Button asChild size="lg" className="amber-glow h-14 px-10 text-base tracking-[0.2em] uppercase font-semibold rounded-lg transition-all duration-300 hover:scale-[1.02] bg-primary text-primary-foreground">
            <Link to="/ServicesPage">CONTACT US<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-1 h-5 w-5" aria-hidden="true" data-ast-id="f:/src/pages/PropertyCleanup/sections/ReadyToTransformYourProperty.tsx|sig:8oss9r" data-ast-start="4319" data-ast-end="4358"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
          </Button>

          <p className="font-default text-sm text-foreground/60">
            or call{" "}
            <Link to="tel:+15555550142" className="text-foreground/80 hover:text-foreground transition-colors underline-offset-4 hover:underline">
              (503) 555-0142
            </Link>{" "}
            — let's discuss your project.
          </p>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 z-10 hidden sm:block">
        <div className="font-mono text-[10px] sm:text-xs tracking-[0.2em] uppercase border-l-2 pl-3 py-1" style={{
        color: "var(--primary)",
        borderColor: "var(--primary)"
      }}>
          <div className="text-foreground/50 mb-1">Next Available Crew</div>
          <div className="text-foreground/90">
            {String(countdown.days).padStart(2, "0")} Days, {String(countdown.hours).padStart(2, "0")} Hours
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 sm:hidden">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-center" style={{
        color: "var(--primary)"
      }}>
          <div className="text-foreground/50 mb-1">Next Available</div>
          <div className="text-foreground/90">
            {String(countdown.days).padStart(2, "0")}D {String(countdown.hours).padStart(2, "0")}H
          </div>
        </div>
      </div>
    </section>;
}